// This component is compatible with SSR but uses client-side features
'use client';

import React, { memo, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { useFiltersStore, useBookingStore } from '../../../BookingJourney/context';
import { BookingDatePicker } from '../../../BookingJourney/components/landing/hero/filters/BookingDatePicker';
import { getCaption } from '../../../BookingJourney/helpers/getCaption';
import { formatAndSortDates, getMultipleDateConstraints } from '../../../BookingJourney/helpers/datePickerUtils';
import { filterFutureDates } from '../../../BookingJourney/helpers/dateValidation';
// Define formatDate function directly to avoid import error
const formatDate = (date: Date | undefined): string | null => {
  if (!date) return null;
  return format(date, 'yyyy-MM-dd');
};
type DateSearchFieldProps = {
  showArrow?: boolean;
};

// Using memo to prevent unnecessary re-renders
const DateSearchField = memo(({
  showArrow = false
}: DateSearchFieldProps) => {
  // Get state from stores using selectors to minimize re-renders
  const startDate = useFiltersStore(state => state.filters.startDate);
  const endDate = useFiltersStore(state => state.filters.endDate);
  const service = useFiltersStore(state => state.filters.service);
  const setFilters = useFiltersStore(state => state.setFilters);
  const selectedDays = useBookingStore(state => state.schedule.selectedDays);
  const setSchedule = useBookingStore(state => state.setSchedule);

  // Filter out past dates
  const filteredSelectedDays = filterFutureDates(selectedDays || []);

  // Ensure selected dates are properly persisted during navigation
  // This addresses an issue where dates selected here weren't being properly transferred
  // to the BookingJourney component during navigation
  useEffect(() => {
    const hasRemovedDates = selectedDays.length !== filteredSelectedDays.length;
    if (hasRemovedDates) {
      setSchedule({
        selectedDays: filteredSelectedDays
      });
    }
  }, [selectedDays, filteredSelectedDays, setSchedule]);

  // Determine if we're using single, multiple, or range date picker
  const isWalkOrDropIn = ['walk', 'drop-in', 'wag-walk'].includes(service || '');
  return <div className="w-full">
      {isWalkOrDropIn ? <BookingDatePicker showArrow={showArrow} iconColor="#00BF8F" calendarProps={{
      mode: 'multiple',
      selected: filteredSelectedDays.length ? filteredSelectedDays.map(selectedDate => parseISO(selectedDate)) : [],
      disabled: getMultipleDateConstraints(filteredSelectedDays),
      onSelect: dates => {
        // This ensures dates are saved in the same location as DateFilters.tsx
        // and will be properly available after navigation
        setSchedule({
          selectedDays: formatAndSortDates(dates)
        });
      }
    }} clearDates={() => {
      setSchedule({
        selectedDays: []
      });
    }} align="center" caption={getCaption({
      type: 'multiple',
      selectedDays: filteredSelectedDays
    })} triggerButtonClass="border-none bg-transparent pl-0" dateCaptionClassName={`${filteredSelectedDays.length > 0 ? 'text-darkGray' : 'text-[#727272]'} font-['Hero New'] text-[13px] font-normal leading-[20px] tracking-[0px] pl-[3px] opacity-100`} /> : <BookingDatePicker showArrow={showArrow} iconColor="#00BF8F" calendarProps={{
      mode: 'range',
      selected: {
        from: startDate && parseISO(startDate),
        to: endDate && parseISO(endDate)
      },
      disabled: [{
        before: new Date()
      }],
      onSelect: (range, selectedDay, _activeModifiers, _e) => {
        // Get disabled days (just using the before:today constraint here)
        const disabledDays = [{
          before: new Date()
        }];

        // Check if selected day is disabled
        const isAnyDateDisabled = (day, disabledDates) => {
          if (!day) return false;

          // Create a normalized copy of the selected day (set to start of day)
          const normalizedSelectedDay = new Date(day);
          normalizedSelectedDay.setHours(0, 0, 0, 0);

          // For simple before/after rules
          return disabledDates.some(rule => {
            if (rule.before) {
              // Create a normalized copy of the before date (set to start of day)
              const normalizedBeforeDate = new Date(rule.before);
              normalizedBeforeDate.setHours(0, 0, 0, 0);

              // Compare dates without time components
              // This allows selecting the current day
              if (normalizedSelectedDay < normalizedBeforeDate) return true;
            }
            if (rule.after) {
              // Create a normalized copy of the after date (set to start of day)
              const normalizedAfterDate = new Date(rule.after);
              normalizedAfterDate.setHours(0, 0, 0, 0);

              // Compare dates without time components
              if (normalizedSelectedDay > normalizedAfterDate) return true;
            }
            return false;
          });
        };
        if (isAnyDateDisabled(selectedDay, disabledDays)) {
          return;
        }
        let newStartDate;
        let newEndDate;
        if (startDate && endDate) {
          newStartDate = formatDate(selectedDay);
          newEndDate = undefined;
        } else {
          newStartDate = formatDate(range?.from);
          newEndDate = formatDate(range?.to);
        }
        setFilters({
          startDate: newStartDate,
          endDate: newEndDate
        });
      }
    }} align="center" caption={getCaption({
      type: 'range',
      startDate,
      endDate
    })} triggerButtonClass="border-none bg-transparent pl-0" dateCaptionClassName={`${startDate && endDate ? 'text-darkGray' : 'text-[#727272]'} font-['Hero New'] text-[13px] font-normal leading-[20px] tracking-[0px] pl-[3px] opacity-100`} />}
    </div>;
});

// Add display name for debugging
DateSearchField.displayName = 'DateSearchField';
export default DateSearchField;